import React from "react";
import { NavigateFunction } from "react-router-dom";
import MenuSection from "./MenuSection";
import type { MenuItem } from "../MenuData";
import { ProductInternalName } from "../productConfig";
import {
  insightMenuItems,
  managementItems,
} from "../MenuData";

export type SideMenuNavByProductProps = {
  internalName: string;
  isExpanded: boolean;
  isMobileView: boolean;
  expandedSections: { [key: string]: boolean };
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  activeItem: string;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  navigate: NavigateFunction;
  computedMainItems: MenuItem[];
  filteredServiceItems: MenuItem[];
  eventManagementItemsForMenu: MenuItem[];
  reflectorV2MenuSections: MenuItem[];
  /** Account area — Personal, Organization and API — already filtered. */
  accountSections: MenuItem[];
};

/**
 * Main product nav: Overview / Services / Monitoring / Management, closed by
 * the shared account area (CTX-512).
 *
 * The account area is appended here rather than inside each product's item list
 * because it is the same for all of them — a product that forgot to include it
 * would silently lose the rows.
 */
const SideMenuNavByProduct: React.FC<SideMenuNavByProductProps> = ({
  internalName,
  isExpanded,
  isMobileView,
  expandedSections,
  setExpandedSections,
  activeItem,
  setActiveItem,
  navigate,
  computedMainItems,
  filteredServiceItems,
  eventManagementItemsForMenu,
  reflectorV2MenuSections,
  accountSections,
}) => {
  const showLabels = isExpanded || isMobileView;
  const onPathNavigate = (item: MenuItem) => {
    if (item.path) navigate(item.path);
  };

  /* Every product closes with the same account area, so it is built once here
     and rendered by each branch below. An entry with children is a titled group
     (Personal, Organization); one without is a plain row (API) — the same rule
     the services area uses, so the lone row never becomes a header echoing its
     own label. Empty only if a tenant is gated out of every row. */
  const accountArea = accountSections.map((section) => (
    <MenuSection
      key={section.id ?? section.label}
      title={showLabels && section.children ? section.label : ""}
      items={section.children ?? [section]}
      expandedSections={expandedSections}
      setExpandedSections={setExpandedSections}
      activeItem={activeItem}
      setActiveItem={setActiveItem}
      isExpanded={showLabels}
      onItemClick={(item) => onPathNavigate(item)}
    />
  ));

  if (internalName === ProductInternalName.CtxReflectorV2) {
    return (
      <>
        {reflectorV2MenuSections.map((filteredSection) => (
          <MenuSection
            key={filteredSection.id || filteredSection.label}
            title={showLabels ? filteredSection.label : ""}
            items={
              filteredSection.children ? filteredSection.children : [filteredSection]
            }
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            isExpanded={showLabels}
            onItemClick={(item) => onPathNavigate(item)}
          />
        ))}
        {accountArea}
      </>
    );
  }

  if (internalName === ProductInternalName.Insight) {
    return (
      <>
        {insightMenuItems
          .filter((section) => {
            return (
              section.label?.toLowerCase() !== "overview" &&
              section.id?.toLowerCase() !== "overview"
            );
          })
          .map((section) => (
            <MenuSection
              key={section.id || section.label}
              title={showLabels ? section.label : ""}
              items={section.children ? section.children : [section]}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              isExpanded={showLabels}
              onItemClick={(item) => onPathNavigate(item)}
            />
          ))}
        {accountArea}
      </>
    );
  }

  /**
   * The sections the services area renders as.
   *
   * Kontrol Edge groups its catalogue by connectivity layer (CTX-660), so each
   * row of `filteredServiceItems` is a layer and becomes a section of its own —
   * the shape Reflector V2 and Insight already use above. It has to be a
   * section rather than a collapsible `MenuItem`: collapsed, a `MenuSection`
   * drops its title and renders the services as the flat strip of icons the
   * rail has always shown, while a collapsed parent row would paint only its
   * own icon — which a layer does not have — and never mount the services.
   *
   * Reflector v1 has no layers, so it keeps the single `Services` section it
   * has today.
   */
  const serviceSections: MenuItem[] =
    internalName === ProductInternalName.KontrolEdge
      ? filteredServiceItems
      : [{ label: "Services", id: "services", children: filteredServiceItems }];

  // Kontrol Edge (and CTX Reflector v1).
  return (
    <>
      {/* Overview renders as a standalone item (no "Main" header) per the redesign. */}
      <MenuSection
        title=""
        items={computedMainItems}
        expandedSections={expandedSections}
        setExpandedSections={setExpandedSections}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onItemClick={(item) => onPathNavigate(item)}
        isExpanded={showLabels}
      />
      {serviceSections.map((section) => (
        <MenuSection
          key={section.id ?? section.label}
          /* A section with no children is a lone service, not a group: it
             renders as a plain row rather than as a header echoing its own
             label. */
          title={showLabels && section.children ? section.label : ""}
          items={section.children ?? [section]}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          isExpanded={showLabels}
          onItemClick={(item) => onPathNavigate(item)}
        />
      ))}
      {eventManagementItemsForMenu.length > 0 && (
        <MenuSection
          title={showLabels ? "Monitoring" : ""}
          items={eventManagementItemsForMenu}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          isExpanded={showLabels}
          onItemClick={(item) => onPathNavigate(item)}
        />
      )}
      {internalName === ProductInternalName.CtxReflector && (
        <MenuSection
          title={showLabels ? "Management" : ""}
          items={managementItems}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          isExpanded={showLabels}
          onItemClick={(item) => onPathNavigate(item)}
        />
      )}
      {accountArea}
    </>
  );
};

export default SideMenuNavByProduct;
